import { scoreQueryOptions, scoreRefreshIntervalMs } from "./scoreQueryOptions";
/**
 * One league, one slice of the season at a time (Scores UX spec v1.0 section
 * 4): the date bar (a calendar circle and the strip, which are one state with
 * the calendar), then ONE section card whose header is the human day heading
 * ("Monday, Oct 19") with the Standings button in its trailing slot, then the
 * game rows. Two columns at `lg`, with the calendar inline beside the card.
 *
 * The league's name is not repeated in the body: the selected chip in the row
 * above already names it (fixes web L1). Pinning lives in the league menu, so
 * the body carries no star (fixes web L2). A raw ISO day is never rendered.
 *
 * The slice is a DAY for most leagues and a WEEK for a league whose season
 * runs in weeks (ISSUE-950). Which one a league gets is decided by its catalog
 * sport, never by a list of league names: see `WEEK_BASED_SPORT_KEY`. The two
 * paths share this pane's frame, its heading slots and its phases, and nothing
 * else. Every day read is held until the catalog has answered, so a week-based
 * league never fires the 62-day scan and never flashes a day strip first.
 *
 * At `lg` a day-based league hides its strip, because the inline calendar
 * selects a day. A week-based league keeps its week strip on screen at every
 * width, above the heading and beside the calendar, and the calendar marks the
 * whole selected week (ISSUE-958).
 *
 * No back affordance: the league chip row the route mounts above this pane
 * stays on screen and is the way back to the `All` feed (ISSUE-949).
 *
 * A football league whose weeks read answers with no weeks at all is shown by
 * day instead (ISSUE-976), so its calendar and strip always have something to
 * select. The empty line comes from buildEmptyLine and always reads as a whole
 * sentence.
 *
 * A league the catalog marks `comingSoon` (golf and MMA at launch, SCORES-3)
 * keeps the header and shows the shared Coming soon screen in place of the
 * strip, calendar and list, and makes no events read (ISSUE-1020). The flag
 * decides, never a league name, so switching it in Staff is the whole change.
 * Until the catalog answers, every league shows the header and a skeleton
 * only, so a shared Coming soon link never paints a strip first.
 */
import {
  useEventDaysWithGamesList,
  useEventLastPlayedGet,
  useEventList,
  useEventWeekList,
  useEventWeeksWithGamesList,
} from "@sportsmet/events-sdk/private";
import {
  useLeagueCatalogGet,
  useLeagueConferenceList,
} from "@sportsmet/sports-sdk/private";
import { useProfileGet } from "@sportsmet/profile-sdk/private";
import { weekHeadingLabel } from "@sportsmet/theme/scores";
import { Button, cn, Dialog, FOCUS_RING } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { CalendarIcon } from "../../icons/glyphs";
import { ComingSoon } from "../gameDetail/ComingSoon";
import { StandingsLinkButton } from "../standings/StandingsLinkButton";
import {
  firstDayOfMonth,
  lastDayOfMonth,
  longDayLabel,
  shiftDay,
  shortDayLabel,
  todayInZone,
} from "./calendarDay";
import {
  CONFERENCE_ALL_LABEL,
  conferenceEmptyLine,
  ConferenceFilter,
  conferencePillLabel,
} from "./ConferenceFilter";
import { resolveListedConferenceKey } from "./conferenceKeyAliases";
import { DateStrip } from "./DateStrip";
import { buildEmptyLine } from "./emptyLine";
import { sortFavoritesFirst } from "./favoriteScores";
import { resolveLandingDay } from "./landingDay";
import { resolveLandingWeek } from "./landingWeek";
import {
  CALENDAR_DIALOG_TITLE,
  goToDayLabel,
  JUMP_TO_TODAY_LABEL,
  LEAGUE_COMING_SOON_BODY,
  noGamesOnLabel,
} from "./leagueSelectorContract";
import { MonthCalendarCard, MonthCalendarGrid } from "./MonthCalendar";
import { ScoreRow } from "./ScoreRow";
import { countInPlay, ScoresSectionCard } from "./ScoresSectionCard";
import {
  ScoresSectionFailed,
  ScoresSectionLoading,
} from "./ScoresSectionStates";
import {
  findWeekContainingDay,
  WEEK_BASED_SPORT_KEY,
  weekLabel,
} from "./seasonWeek";
import { resolveViewerTimeZone } from "./viewerTimeZone";
import { WeekStrip } from "./WeekStrip";

export type typeLeagueDrillDownPaneProps = {
  leagueId: string;
  /** The `?date=` value, or undefined. When undefined the landing rule
   *  decides. On a week-based league this is not the selection; it only seeds
   *  which week an already-shared day link opens on. */
  date: string | undefined;
  /** The `?week=` value, or undefined. Ignored on a league that is not
   *  week-based, and ignored when it is not a whole week number. */
  week: number | undefined;
  /** True when ?sheet=calendar. Drives the overlay, never component state. */
  isCalendarOpen: boolean;
  onSelectDay: (day: string) => void;
  onSelectWeek: (seasonWeek: number) => void;
  onOpenCalendar: () => void;
  onCloseCalendar: () => void;
  onOpenGame?: ((eventId: string) => void) | undefined;
  /** The `?conference=` value (ISSUE-1387). Applied only once the league's
   *  conference list names it. */
  conference?: string | undefined;
  /** True when ?sheet=conference. */
  isConferenceOpen?: boolean | undefined;
  onOpenConference?: (() => void) | undefined;
  onCloseConference?: (() => void) | undefined;
  onSelectConference?:
    ((conferenceKey: string | undefined) => void) | undefined;
};

export const LeagueDrillDownPane = (
  props: typeLeagueDrillDownPaneProps,
): ReactNode => {
  const timeZone = resolveViewerTimeZone();
  const todayDay = todayInZone({ nowMs: Date.now(), timeZone });

  const catalog = useLeagueCatalogGet({});
  const profile = useProfileGet({});

  const sport = catalog.data?.data?.sports.find((candidate) =>
    candidate.leagues.some((league) => league.leagueId === props.leagueId),
  );
  const leagueOption = sport?.leagues.find(
    (league) => league.leagueId === props.leagueId,
  );
  const shortName = leagueOption?.shortName ?? "";
  const isComingSoon = leagueOption?.visibilityState === "comingSoon";
  const favoriteTeamIds = profile.data?.data?.profile?.favoriteTeamIds ?? [];

  // ISSUE-950 R3. The sport decides, so a football league the catalog gains
  // tomorrow is week-based without a line of code changing here.
  const isWeekBased = !isComingSoon && sport?.sportKey === WEEK_BASED_SPORT_KEY;
  // Both paths wait for the catalog. Letting the day reads run first would put
  // a 62-day scan and a last-played lookup on every football league, and would
  // paint a day strip for one frame before the week strip replaced it. A
  // catalog that fails resolves to no sport, so the league falls back to days.
  const isCatalogSettled = !catalog.isLoading;
  // ISSUE-1020: a Coming soon league has no games to show, so it makes no
  // events read at all.
  const canReadGames = isCatalogSettled && !isComingSoon;

  // 1. the week reads. Season-scoped, not window-scoped, so a week later in
  //    the season than today comes back for free (R11).
  const weeksRead = useEventWeeksWithGamesList(
    { leagueId: props.leagueId, timeZone },
    { enabled: isWeekBased },
  );
  const weeks = weeksRead.data?.data?.weeks ?? [];

  // ISSUE-976 R3. A football league whose weeks read answered with no weeks
  // at all has nothing to show by week, so it is shown by day: the date
  // strip, the landing day and a calendar click that selects the day. A
  // failed read is NOT this case; it stays on the week path and shows the
  // failed state.
  const hasNoWeeks =
    isWeekBased &&
    weeksRead.isSuccess &&
    !weeksRead.data.error &&
    weeks.length === 0;
  const isWeekMode = isWeekBased && !hasNoWeeks;
  const isDayBased = canReadGames && !isWeekMode;

  // 2. the 62-day window around today.
  const nearWindow = useEventDaysWithGamesList(
    {
      leagueId: props.leagueId,
      fromDay: shiftDay({ day: todayDay, deltaDays: -30 }),
      toDay: shiftDay({ day: todayDay, deltaDays: 31 }),
      timeZone,
    },
    { enabled: isDayBased },
  );

  // 3. when did this league last play -- the offseason branch (ISSUE-457).
  const lastPlayed = useEventLastPlayedGet(
    { leagueId: props.leagueId, timeZone },
    { enabled: isDayBased },
  );

  // 4. the landing day, then the selection.
  const landingDay = resolveLandingDay({
    todayDay,
    daysWithGames: nearWindow.data?.data?.days ?? [],
    lastPlayedDay: lastPlayed.data?.data?.lastPlayedDay ?? null,
  });
  const selectedDay = isDayBased ? (props.date ?? landingDay) : null;

  const nearFrom = shiftDay({ day: todayDay, deltaDays: -30 });
  const nearTo = shiftDay({ day: todayDay, deltaDays: 31 });
  const needsFarWindow =
    selectedDay !== null && (selectedDay < nearFrom || selectedDay > nearTo);

  const farWindow = useEventDaysWithGamesList(
    {
      leagueId: props.leagueId,
      fromDay: shiftDay({ day: selectedDay ?? todayDay, deltaDays: -30 }),
      toDay: shiftDay({ day: selectedDay ?? todayDay, deltaDays: 31 }),
      timeZone,
    },
    { enabled: isDayBased && needsFarWindow },
  );
  const stripDays = needsFarWindow
    ? (farWindow.data?.data?.days ?? [])
    : (nearWindow.data?.data?.days ?? []);

  // `?week=` wins, then the week holding an already-shared `?date=`, then the
  // landing rule. A `?week=` the season does not have is still honored, the
  // same way a gameless `?date=` is: the list simply comes back empty.
  const urlWeek =
    props.week !== undefined && Number.isInteger(props.week) && props.week >= 0
      ? props.week
      : null;
  const dateWeek =
    props.date !== undefined
      ? (findWeekContainingDay({ weeks, day: props.date })?.seasonWeek ?? null)
      : null;
  const selectedWeek = isWeekMode
    ? (urlWeek ?? dateWeek ?? resolveLandingWeek({ todayDay, weeks }))
    : null;
  const selectedWeekRange =
    weeks.find((week) => week.seasonWeek === selectedWeek) ?? null;

  const monthAnchorTarget = isWeekMode
    ? (selectedWeekRange?.firstDay ?? null)
    : selectedDay;

  const [monthAnchorDay, setMonthAnchorDay] = useState<string>(
    monthAnchorTarget ?? todayDay,
  );
  // The anchor is seeded on the first render, where the reads that decide the
  // selection are still in flight and the target is therefore null. Re-anchor
  // when the SELECTION itself moves to another month, so a landing day that
  // resolves late (the offseason branch in landingDay.ts) carries the calendar
  // with it. Keyed on the selection changing, never on the anchor, so Previous
  // month and Next month still move the calendar away from the selection.
  // ISSUE-910 R4.
  const [anchoredTargetDay, setAnchoredTargetDay] = useState<string | null>(
    monthAnchorTarget,
  );
  if (monthAnchorTarget !== anchoredTargetDay) {
    setAnchoredTargetDay(monthAnchorTarget);
    if (
      monthAnchorTarget !== null &&
      firstDayOfMonth(monthAnchorTarget) !== firstDayOfMonth(monthAnchorDay)
    ) {
      setMonthAnchorDay(monthAnchorTarget);
    }
  }
  // The calendar stays on both paths, so a week-based league keeps a way to
  // jump to a date (R9). It is the one day read a week-based league makes.
  const monthWindow = useEventDaysWithGamesList(
    {
      leagueId: props.leagueId,
      fromDay: firstDayOfMonth(monthAnchorDay),
      toDay: lastDayOfMonth(monthAnchorDay),
      timeZone,
    },
    { enabled: canReadGames },
  );

  // ISSUE-1387. A college league offers a conference picker. The URL's key
  // goes to the list reads as soon as it is there; once the league's list
  // answers, a key it does not name is dropped rather than emptying the list.
  // A key from before or after the college conference rename resolves to the
  // list's own key for that conference (conferenceKeyAliases.ts), so pac-12
  // and pacific-12 both pick it whichever generation the list answers with.
  const conferenceList = useLeagueConferenceList(
    { leagueId: props.leagueId },
    { enabled: canReadGames, staleTime: 60_000 },
  );
  const conferences = conferenceList.data?.data?.conferences ?? [];
  const requestedConference = props.conference ?? null;
  const conferenceKey =
    requestedConference === null
      ? null
      : conferenceList.data?.data == null
        ? requestedConference
        : resolveListedConferenceKey({
            requestedKey: requestedConference,
            listedKeys: conferences.map(
              (conference) => conference.conferenceKey,
            ),
          });

  const gameList = useEventList(
    {
      leagueId: props.leagueId,
      day: selectedDay ?? "",
      timeZone,
      conferenceKey,
    },
    {
      enabled: isDayBased && selectedDay !== null,
      // Poll at whatever the read itself asked for, and stop when it asks for
      // nothing. Inline rather than shared with the week list below: react-query
      // types the query argument per response payload.
      ...scoreQueryOptions,
      refetchInterval: (query) => {
        const seconds = query.state.data?.data?.refreshAfterSeconds ?? 0;
        return scoreRefreshIntervalMs(seconds);
      },
    },
  );

  const weekGameList = useEventWeekList(
    {
      leagueId: props.leagueId,
      seasonWeek: selectedWeek ?? 0,
      timeZone,
      conferenceKey,
    },
    {
      enabled: isWeekMode && selectedWeek !== null,
      ...scoreQueryOptions,
      refetchInterval: (query) => {
        const seconds = query.state.data?.data?.refreshAfterSeconds ?? 0;
        return scoreRefreshIntervalMs(seconds);
      },
    },
  );

  // Phase is driven by the reads that decide the selection and the list. The
  // far window and the month window only feed the strip and the calendar and
  // never block the list from rendering. The catalog blocks both paths,
  // because until it answers neither the selection nor the strip is known.
  const hasSelection = isWeekMode
    ? selectedWeek !== null
    : selectedDay !== null;
  const isLoading =
    catalog.isLoading ||
    (isWeekMode
      ? weeksRead.isLoading || (hasSelection && weekGameList.isLoading)
      : nearWindow.isLoading ||
        lastPlayed.isLoading ||
        (hasSelection && gameList.isLoading));
  const hasFailed = isWeekMode
    ? weeksRead.isError ||
      Boolean(weeksRead.data?.error) ||
      (hasSelection &&
        weekGameList.data?.data == null &&
        (weekGameList.isError || Boolean(weekGameList.data?.error)))
    : nearWindow.isError ||
      lastPlayed.isError ||
      Boolean(nearWindow.data?.error) ||
      Boolean(lastPlayed.data?.error) ||
      (hasSelection &&
        gameList.data?.data == null &&
        (gameList.isError || Boolean(gameList.data?.error)));

  const listEvents = isWeekMode
    ? (weekGameList.data?.data?.events ?? [])
    : (gameList.data?.data?.events ?? []);
  const events = sortFavoritesFirst({ events: listEvents, favoriteTeamIds });

  const phase: "loading" | "failed" | "neverPlayed" | "noGames" | "ready" =
    isLoading
      ? "loading"
      : hasFailed
        ? "failed"
        : !hasSelection
          ? "neverPlayed"
          : events.length === 0
            ? "noGames"
            : "ready";

  const onRetry = (): void => {
    if (isWeekMode) {
      void weeksRead.refetch();
      void weekGameList.refetch();
      return;
    }
    void nearWindow.refetch();
    void lastPlayed.refetch();
    void gameList.refetch();
  };

  // A day the calendar offers can fall in a bye gap that belongs to no week.
  // Landing on the most recent week before it beats doing nothing, so a tap
  // always moves the screen somewhere.
  const onCalendarSelectDay = (day: string): void => {
    if (!isWeekMode) {
      props.onSelectDay(day);
      return;
    }
    const seasonWeek =
      findWeekContainingDay({ weeks, day })?.seasonWeek ??
      resolveLandingWeek({ todayDay: day, weeks });
    if (seasonWeek !== null) {
      props.onSelectWeek(seasonWeek);
    }
  };

  // The card heading: the human day ("Monday, Oct 19", with the year when it
  // is not this year) or the week and its range ("Week 7, Oct 16-20"). The
  // ISO day is never shown or read aloud.
  const selectionHeading = isWeekMode
    ? selectedWeekRange !== null
      ? weekHeadingLabel(selectedWeekRange)
      : selectedWeek !== null
        ? weekLabel(selectedWeek)
        : ""
    : selectedDay !== null
      ? longDayLabel(selectedDay, todayDay)
      : "";
  const conferenceLabel = conferencePillLabel({ conferences, conferenceKey });
  const emptyLine =
    conferenceKey !== null && conferenceLabel !== CONFERENCE_ALL_LABEL
      ? conferenceEmptyLine({
          label: conferenceLabel,
          selectionLabel: selectionHeading,
        })
      : !isWeekMode && selectedDay !== null
        ? noGamesOnLabel(longDayLabel(selectedDay, todayDay))
        : buildEmptyLine({
            shortName,
            mode: isWeekMode ? "week" : "day",
            selectionLabel: selectionHeading,
          });
  // The empty day's way out: the next game day the strip knows, else today.
  const nextGameDay =
    !isWeekMode && selectedDay !== null
      ? (stripDays.find((day) => day > selectedDay) ?? null)
      : null;
  const emptyAction: { label: string; day: string } | null =
    nextGameDay !== null
      ? { label: goToDayLabel(shortDayLabel(nextGameDay)), day: nextGameDay }
      : !isWeekMode && selectedDay !== null && selectedDay !== todayDay
        ? { label: JUMP_TO_TODAY_LABEL, day: todayDay }
        : null;
  const calendarSelectedDay = isWeekMode
    ? (selectedWeekRange?.firstDay ?? props.date ?? todayDay)
    : (selectedDay ?? todayDay);
  // ISSUE-958. A week-based league marks the whole selected week on the
  // calendar, first day through last. A day-based league passes nothing and
  // keeps its single selected day exactly as before.
  const calendarSelectedLastDay = isWeekMode
    ? selectedWeekRange?.lastDay
    : undefined;

  const strip = isWeekMode ? (
    <WeekStrip
      weeks={weeks}
      selectedWeek={selectedWeek}
      onSelectWeek={props.onSelectWeek}
    />
  ) : (
    <DateStrip
      days={stripDays}
      selectedDay={selectedDay}
      todayDay={todayDay}
      onSelectDay={props.onSelectDay}
    />
  );

  const cardBody =
    phase === "loading" ? (
      <ScoresSectionLoading testId="league-drilldown-loading" />
    ) : phase === "failed" ? (
      <ScoresSectionFailed testId="league-drilldown-failed" onRetry={onRetry} />
    ) : phase === "neverPlayed" || phase === "noGames" ? (
      <div
        className="flex flex-col items-center gap-3 border-t border-hairline px-score-row-inset py-6 text-center"
        data-testid="league-drilldown-empty"
      >
        <p className="text-body text-ink-secondary">{emptyLine}</p>
        {emptyAction !== null ? (
          <Button
            variant="secondary"
            size="sm"
            data-testid="league-drilldown-empty-action"
            onClick={() => {
              props.onSelectDay(emptyAction.day);
            }}
          >
            {emptyAction.label}
          </Button>
        ) : null}
      </div>
    ) : (
      <div data-testid="league-drilldown-list">
        {events.map((event) => (
          <ScoreRow
            key={event.eventId}
            event={event}
            onOpenGame={props.onOpenGame}
            favoriteTeamIds={favoriteTeamIds}
          />
        ))}
      </div>
    );

  return (
    <div data-testid="league-drilldown">
      {catalog.isLoading ? (
        <ScoresSectionCard testId="league-drilldown-catalog-loading" title="">
          <ScoresSectionLoading testId="league-drilldown-catalog-loading-rows" />
        </ScoresSectionCard>
      ) : isComingSoon ? (
        <ComingSoon
          body={LEAGUE_COMING_SOON_BODY}
          testId="league-drilldown-coming-soon"
        />
      ) : (
        <>
          <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-[var(--sm-size-scores-calendar-track)_minmax(0,1fr)] lg:gap-6">
            <div className="hidden lg:block">
              <MonthCalendarCard
                monthAnchorDay={monthAnchorDay}
                onMonthAnchorChange={setMonthAnchorDay}
                daysWithGames={monthWindow.data?.data?.days ?? []}
                todayDay={todayDay}
                selectedDay={calendarSelectedDay}
                selectedLastDay={calendarSelectedLastDay}
                onSelectDay={onCalendarSelectDay}
              />
            </div>

            <div className="flex min-w-0 flex-col gap-3">
              {/* The date bar: a fixed 64px row, [calendar] 8 [strip]. A
                  day-based league hides it at `lg`, where the inline calendar
                  selects the day. A week-based league keeps its week strip at
                  every width and hides only the calendar button there
                  (ISSUE-958). */}
              <div
                className={cn(
                  "flex h-day-strip-height items-center gap-2",
                  isWeekMode ? undefined : "lg:hidden",
                )}
                data-testid="league-drilldown-strip-row"
              >
                <button
                  type="button"
                  className={cn(
                    "flex h-touch-target w-touch-target shrink-0 items-center justify-center rounded-full border border-line bg-surface text-ink",
                    "transition-colors duration-200 hover:bg-surface-sunken motion-reduce:transition-none",
                    isWeekMode ? "lg:hidden" : undefined,
                    FOCUS_RING,
                  )}
                  data-testid="league-drilldown-calendar-trigger"
                  aria-label={CALENDAR_DIALOG_TITLE}
                  onClick={props.onOpenCalendar}
                >
                  <CalendarIcon size="md" />
                </button>
                <div className="min-w-0 flex-1">{strip}</div>
              </div>

              <ConferenceFilter
                conferences={conferences}
                conferenceKey={conferenceKey}
                isOpen={props.isConferenceOpen ?? false}
                onOpen={() => {
                  props.onOpenConference?.();
                }}
                onClose={() => {
                  props.onCloseConference?.();
                }}
                onSelect={(next) => {
                  props.onSelectConference?.(next);
                }}
              />

              <ScoresSectionCard
                testId="league-drilldown-card"
                title={selectionHeading}
                titleTestId="league-drilldown-day-heading"
                liveCount={phase === "ready" ? countInPlay(events) : 0}
                trailing={<StandingsLinkButton leagueId={props.leagueId} />}
              >
                {cardBody}
              </ScoresSectionCard>
            </div>
          </div>

          <Dialog
            open={props.isCalendarOpen}
            onOpenChange={(next) => {
              if (!next) props.onCloseCalendar();
            }}
            title={CALENDAR_DIALOG_TITLE}
          >
            <MonthCalendarGrid
              monthAnchorDay={monthAnchorDay}
              onMonthAnchorChange={setMonthAnchorDay}
              daysWithGames={monthWindow.data?.data?.days ?? []}
              todayDay={todayDay}
              selectedDay={calendarSelectedDay}
              selectedLastDay={calendarSelectedLastDay}
              onSelectDay={(day) => {
                onCalendarSelectDay(day);
                props.onCloseCalendar();
              }}
            />
          </Dialog>
        </>
      )}
    </div>
  );
};
